import React from 'react';
import { Close, Download } from '@mui/icons-material';
import { GeneratedImage } from '../types';

interface ImageLightboxProps {
  image: GeneratedImage;
  imageIndex: number;
  isOpen: boolean;
  onClose: () => void;
}

const ImageLightbox: React.FC<ImageLightboxProps> = ({ image, imageIndex, isOpen, onClose }) => {
  if (!isOpen) return null;

  const handleDownload = async () => {
    try {
      let downloadUrl: string;
      let shouldRevokeUrl = false;
      
      if (image.base64) {
        // Convert base64 to blob for reliable download
        const byteCharacters = atob(image.base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: `image/${image.format}` });
        downloadUrl = URL.createObjectURL(blob);
        shouldRevokeUrl = true;
      } else if (image.url) {
        // For regular URLs, try to fetch and convert to blob for consistent download behavior
        try {
          const response = await fetch(image.url);
          const blob = await response.blob();
          downloadUrl = URL.createObjectURL(blob);
          shouldRevokeUrl = true;
        } catch (fetchError) {
          console.warn('Failed to fetch image for download, using direct URL:', fetchError);
          // If fetch fails, fall back to direct URL
          downloadUrl = image.url;
        }
      } else {
        console.error('No image data available for download');
        return;
      }
      
      const link = document.createElement('a');
      link.href = downloadUrl;
      // Use timestamp to ensure unique filenames for multiple image downloads
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19); // Format: 2024-01-15T14-30-25
      link.download = `generated-image-${timestamp}.${image.format}`;
      // Remove target="_blank" to prevent opening in new tab
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the object URL if we created one
      if (shouldRevokeUrl) {
        // Delay revoke to ensure download starts
        setTimeout(() => {
          URL.revokeObjectURL(downloadUrl);
        }, 100);
      }
    } catch (error) {
      console.error('Error downloading image:', error);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div 
      className="image-lightbox-overlay"
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '20px'
      }}
    >
      <div 
        className="image-lightbox-container"
        style={{
          position: 'relative',
          maxWidth: '90vw',
          maxHeight: '90vh',
          backgroundColor: 'white',
          borderRadius: '8px',
          overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
        }}
      >
        {/* Header with controls */}
        <div 
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            zIndex: 1,
            display: 'flex',
            gap: '8px',
            padding: '12px',
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.5), transparent)'
          }}
        >
          <button
            onClick={handleDownload}
            style={{
              background: 'rgba(255, 255, 255, 0.9)',
              border: 'none',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 1)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.9)'}
            title="Download image"
          >
            <Download sx={{ fontSize: 20, color: '#333' }} />
          </button>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255, 255, 255, 0.9)',
              border: 'none',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 1)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.9)'}
            title="Close"
          >
            <Close sx={{ fontSize: 20, color: '#333' }} />
          </button>
        </div>

        {/* Image */}
        <img 
          src={image.url || `data:image/${image.format};base64,${image.base64}`}
          alt="Generated image"
          style={{ 
            width: '100%', 
            height: 'auto',
            maxWidth: '90vw',
            maxHeight: '90vh',
            display: 'block',
            objectFit: 'contain'
          }}
        />
      </div>
    </div>
  );
};

export default ImageLightbox; 